import { createSlice, current } from "@reduxjs/toolkit";

export const TOOL_BAR_ITEM = {
  POINTER: "POINTER",
  AUTO: "AUTO",
  POLYGON: "POLYGON",
  TAG: "TAG",
  BOUNDING_BOX: "BOUNDING_BOX",
  SELECT: "SELECT",
  ASSIGN_TAG: "ASSIGN_TAG",
};

/**
 *
 * @returns {import("../types").Pages}
 */
export const getPageDefaults = () => ({
  annotations: [],
  filters: {
    hue: 0,
    blur: 0,
    noise: 0,
    sepia: 0,
    contrast: 0,
    exposure: 0,
    grayscale: 0,
    brightness: 0,
    saturation: 0,
  },
  x: 0,
  y: 0,
  zoom: 1,
  history: [],
  type: "image",
  image_url: "",
  hasUpdates: false,
  historyIndex: -1,
});
/**
 *
 * @returns {import("../types").Project}
 */
const getProjectDefaults = () => ({
  tags: [],
  images: [],
  stats: {
    total_images: 0,
    total_annotations: 0,
    annotated_images: 0,
    model_name: "",
    model_type: "none",
    un_annotated: 0,
    retrain: false,
    average_image_size: "",
  },

  // Toolbar toggle
  activeTool: TOOL_BAR_ITEM.SELECT,
  activePage: 0,
  lastPageIndex: 0,
  totalImage: 0,
  hasStatsUpdates: false,
  hasTagUpdates: false,

  pages: {
    0: getPageDefaults(),
  },
  selectedItemId: null,
});

const merger = (previous, result) => ({ ...previous, ...result });
export function getFileType(fileExtension) {
  const imageExtensions = new Set(["jpeg", "jpg", "png", "gif", "bmp"]); // Add more image extensions as needed
  const videoExtensions = new Set(["mp4", "avi", "mov", "mkv"]); // Add more video extensions as needed

  const lowerCaseExtension = fileExtension.toLowerCase();

  if (imageExtensions.has(lowerCaseExtension)) {
    return "image";
  } else if (videoExtensions.has(lowerCaseExtension)) {
    return "video";
  } else {
    return "unknown";
  }
}

/**
 * @type {import("../types").LabelCraftState}
 */
const initialState = {
  projects: {},
  projectId: null,
};

export const labelCraftSlice = createSlice({
  name: "labelcraft",
  initialState,
  reducers: {
    /**
     * This function creates a new page to the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type: string, payload: { projectId: number } }} action
     */
    addProject: (state, action) => {
      const { projectId } = action.payload;

      if (!projectId) return;

      state.projectId = projectId;
      state.projects[projectId] = getProjectDefaults();
    },

    /**
     * This function set the entire project at once.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: { projectId: number, project: import("../types").Project } }} action
     */
    setProject: (state, action) => {
      const { projectId, project } = action.payload;

      if (!projectId) return;

      state.projects[projectId] = project;
      state.projectId = projectId;
    },

    /**
     * This function appends a new tag object to the previous list.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Tag  }} action
     */
    addTag: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];
      const tags = project.tags ?? []

      state.projects[projectId].tags = [...tags, action.payload];
    },

    setTagUpdates: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];

      state.projects[projectId].hasTagUpdates = true;
    },

    // addTag: (state, action) => {
    //   const projectId = state.projectId;
    //   const project = state.projects?.[projectId];

    //   state.projects[projectId].tags = [...project.tags, action.payload];
    // },

    /**
     * This function appends a new tag object to the previous list.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Tag  }} action
     */
    unsetTag: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];

      const updates = project.tags.filter(
        (item) => item.name !== action.payload
      );

      state.projects[projectId].tags = updates;
    },

    /**
     * This function appends all tag objects to the tags state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Tag[]  }} action
     */
    setTags: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].tags = action.payload;
    },

    resetTagUpdates: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].hasTagUpdates = false;
    },

    /**
     * This function add all url of images to the previous images list.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Images[]  }} action
     */
    setImages: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];

      state.projects[projectId].images = action.payload;
    },

    setTotalImages: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];

      state.projects[projectId].totalImage = action.payload;
    },

    /**
     * This function add all url of images to the previous images list.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Images[]  }} action
     */
    addImages: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];

      state.projects[projectId].images = [...project.images, ...action.payload];
    },

    /**
     * This function add all url of images to the previous images list.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: string  }} action
     */
    unsetImage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];
      const images = project.images;

      const newImages = images.filter((item) => item.id !== action.payload);

      state.projects[projectId].images = newImages;
    },

    /**
     * This function set the active tool on the toolbar..
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Project["activeTool"]  }} action
     */
    setActiveTool: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];

      if (project.selectedItemId && action.payload !== TOOL_BAR_ITEM.SELECT) {
        state.projects[projectId].selectedItemId = null;
      }

      state.projects[projectId].activeTool = action.payload;
    },

    /**
     * This function add a new page to the previous pages.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Page  }} action
     */
    addPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];
      const pageIndex = project.lastPageIndex + 1;
      const pageIndexUrl = project.pages[0].image_url;

      const payload = action.payload
        ? { ...getPageDefaults(), ...action.payload }
        : getPageDefaults();

      const startInitial = action.payload && pageIndexUrl.length === 0;

      state.projects[projectId].lastPageIndex = pageIndex;
      state.projects[projectId].pages[pageIndex] = payload;
    },

    /**
     * This function set all pages to the pages state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Project["pages"]  }} action
     */
    setPages: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].pages = action.payload;
    },

    /**
     * This function update a single page's (active page) properties.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Page  }} action
     */
    updatePage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];
      const activePage = project?.activePage;
      const page = project?.pages?.[activePage];

      if (!page) return;

      state.projects[projectId].pages[activePage] = merger(
        page,
        action.payload
      );
    },

    /**
     * This function create a new page with image url not
     * currently opened under pages.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: undefined  }} action
     */

    setQuickPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const images = project.images;
      const pageIndex = project.lastPageIndex + 1;

      const pageImages = Object.values(project.pages).map(
        (page) => page.image_url
      );

      const image = images.find(
        (image) => !pageImages.includes(image.image_url)
      );

      state.projects[projectId].pages[pageIndex] = {
        ...getPageDefaults(),
        image_url: image.image_url,
        id: image.id,
        type: image.type,
      };
      state.projects[projectId].activePage = pageIndex;
      state.projects[projectId].lastPageIndex = pageIndex;
    },

    /**
     * This function updates the active page currently viewed or
     * to be viewed.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: number  }} action
     */
    setPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].activePage = action.payload;
    },

    prevPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const pageIndex = project.activePage - 1;

      state.projects[projectId].activePage =
        project.activePage === 0 ? 0 : pageIndex;
    },

    nextPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const pageIndex = project.activePage + 1;

      if (project.activePage === Object.keys(project.pages ?? {}).length - 1)
        return;

      state.projects[projectId].activePage = pageIndex;
    },

    /**
     * This function unset or removes the page with the page index.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: number  }} action
     */
    unSetPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const pages = project.pages;

      delete pages[action.payload];

      state.projects[projectId].pages = pages;
    },

    /**
     * This function updates the filter object property.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Filters  }} action
     */
    setFilters: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const filters = project.pages[activePage].filters;

      state.projects[projectId].pages[activePage].filters = merger(
        filters,
        action.payload
      );
    },

    /**
     * This function add a new annotation to the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: { type: import("../types").AnnotationType, data: any  }  }} action
     */
    setAnnotationMarker: (state, action) => {
      const { data, type } = action.payload;
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;

      if (data?.height === 0 || data?.width === 0) return;

      const annotate = {
        label: "",
        id: `Annotate-${Math.random()}`,
        type,
        [type]: data,
      };

      state.projects[projectId].pages[activePage].hasUpdates = true;
      state.projects[projectId].pages[activePage].annotations.push(annotate);
    },

    /**
     * This function update existing annotation properties to the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Annotation  }} action
     */
    updateAnnotationMarker: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      
      const otherAnnotations = page.annotations.filter(
        (item) => item.id !== action.payload?.id
      );
      
      state.projects[projectId].pages[activePage].hasUpdates = true;
      state.projects[projectId].pages[activePage].annotations = [
        ...otherAnnotations,
        action.payload,
      ];
    },

    /**
     * This function update existing annotation to the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Annotation  }} action
     */
    updateAnnotation: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const annotations = project.pages[activePage].annotations;

      state.projects[projectId].pages[activePage].annotations = [
        ...annotations,
      ];
    },

    /**
     *
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: {x: number, y: number, id: string}[] }} action
     */
    updateAnnotationPosition: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const updatedAnnotates = page.annotations.map((item) => {
        const matchingObject = action.payload?.find(
          (position) => position.id === item.id
        );
        return matchingObject
          ? {
            ...item,
            bounding_box: {
              ...item[item?.type],
              ...matchingObject,
              id: item[item?.type]?.id,
            },
          }
          : item;
      });

      state.projects[projectId].pages[activePage].hasUpdates = true;
      state.projects[projectId].pages[activePage].annotations =
        updatedAnnotates;
    },

    updateAnnotationPolygonPosition: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const updatedAnnotates = page.annotations.map((item) => {
        const matchingObject = action.payload?.find(
          (position) => position.id === item.id
        );

        return matchingObject
          ? {
              ...item,
              polygon: {
                ...item[item?.type],
                ...matchingObject,
                id: item[item?.type]?.id,
              },
            }
          : item;
      });

      state.projects[projectId].pages[activePage].hasUpdates = true;
      state.projects[projectId].pages[activePage].annotations =
        updatedAnnotates;
    },

    setSelectedItemId: (state, action) => {
      const projectId = state.projectId;

      state.projects[projectId].selectedItemId = action.payload;
    },

    resetAnnotations: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;

      state.projects[projectId].pages[activePage].annotations = [];
    },

    /**
     * This function update existing annotation to the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type, payload: import("../types").Stats  }} action
     */
    setStats: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].stats = action.payload;
    },

    /**
     * This function update existing annotation to the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type: string, payload: import("../types").Stats  }} action
     */
    addStats: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const stats = project.stats;

      const prevStats = {
        annotated: stats.annotated,
        model_name: stats.model_name,
        model_type: stats.model_type,
        retrain: stats.retrain,
        un_annotated: stats.un_annotated,
        average_image_size: stats.average_image_size,
      };

      state.projects[projectId].stats = merger(prevStats, action.payload);
    },

    setStatsUpdates: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].hasStatsUpdates = true;
    },

    resetStatsUpdate: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].hasStatsUpdates = false;
    },

    /**
     * This function removes existing annotation from the state.
     * @param {import("../types").LabelCraftState} state
     * @param {{ type: string, payload: undefined  }} action
     */
    unsetAnnotates: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects?.[projectId];
      const activePage = project.activePage;
      const page = project.pages?.[activePage];
      const id = project.selectedItemId;

      if (!id) return;

      const updatedAnnotates = page.annotations.filter(
        (item) => item.id !== id
      );

      state.projects[projectId].pages[activePage].hasUpdates = true;
      state.projects[projectId].pages[activePage].annotations =
        updatedAnnotates;
    },

    onZoomIn: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const updatedZoom = page.zoom + 0.1;

      state.projects[projectId].pages[activePage].zoom = updatedZoom;
    },

    onZoomOut: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const updatedZoom = page.zoom - 0.1;

      state.projects[projectId].pages[activePage].zoom = updatedZoom;
    },

    addHistory: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const { history, historyIndex, ...rest } = page;

      const newPage = { ...rest };
      const newHistory = [...history, newPage];

      state.projects[projectId].pages[activePage] = merger(page, {
        history: newHistory,
        historyIndex: historyIndex + 1,
      });
    },

    unsetHistory: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const { history, historyIndex, ...rest } = page;

      const newHistory = history[historyIndex - 1];

      state.projects[projectId].pages[activePage] = merger(page, {
        ...newHistory,
        history,
        historyIndex: historyIndex - 1,
      });
    },

    setHistory: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;
      const page = project.pages[activePage];

      const { history, historyIndex, ...rest } = page;

      const newHistory = history[historyIndex + 1];

      state.projects[projectId].pages[activePage] = merger(page, {
        ...newHistory,
        history,
        historyIndex: historyIndex + 1,
      });
    },

    resetHasUpdates: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;

      state.projects[projectId].pages[activePage].hasUpdates = false;
    },

    resetAllHasUpdates: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];
      const activePage = project.activePage;

      state.projects[projectId].pages = Object.values(project.pages).map(
        (page) => (page.hasUpdates ? { ...page, hasUpdates: false } : page)
      );
    },

    unSetProject: (state, action) => {
      const projectId = state.projectId;
      const projects = state.projects;

      delete projects[projectId];

      state.projects = projects;
      state.projectId = null;
    },

    resetPages: (state, action) => {
      state.projects[projectId].pages = {
        0: getPageDefaults(),
      };
    },

    setResetPage: (state, action) => {
      const projectId = state.projectId;
      const projects = state.projects;
      const project = state.projects[projectId];

      state.projects[projectId].pages[project.activePage].annotations = [];
    },
  },
});

const { actions } = labelCraftSlice;
export const {
  addTag,
  setPage,
  addPage,
  setPages,
  setImages,
  addImages,
  setFilters,
  updatePage,
  setActiveTool,
  setAnnotationMarker,
  updateAnnotation,
  setSelectedItemId,
  updateAnnotationMarker,
  resetAnnotations,
  unsetAnnotates,
  updateAnnotationPosition,
  onZoomIn,
  onZoomOut,
  addHistory,
  unsetHistory,
  setHistory,
  addProject,
  unSetPage,
  setTags,
  addStats,
  setStats,
  resetHasUpdates,
  resetAllHasUpdates,
  resetPages,
  unsetTag,
  nextPage,
  prevPage,
  setProject,
  setQuickPage,
  unSetProject,
  unsetImage,
  setTotalImages,
  resetStatsUpdate,
  resetTagUpdates,
  setTagUpdates,
  setStatsUpdates,
  setResetPage,
  updateAnnotationPolygonPosition
} = actions;

/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectProjects = (state) => state.labelcraft.projects;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectProjectId = (state) => state.labelcraft.projectId;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectTags = (state) =>
  state.labelcraft.projects[state?.labelcraft?.projectId]?.tags;
export const selectTotalImage = (state) =>
  state.labelcraft.projects[state?.labelcraft?.projectId]?.totalImage;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectDatasets = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.images;

/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectActiveTool = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.activeTool;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectPages = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.pages;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectActivePage = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.activePage;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectSelectedId = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.selectedItemId;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectCurrentAnnotationPage = (state) => (page) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.pages?.[page]
    ?.annotations;
/**
 *
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectZoom = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.pages[
    state.labelcraft.projects[state.labelcraft.projectId].activePage
  ]?.zoom;
/**
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectStats = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.stats;
/**
 * @param {{ labelcraft: import("../types").LabelCraftState }} state
 * @returns
 */
export const selectHasAnnotationUpdates = (state) =>
  state.labelcraft.projects[state.labelcraft.projectId]?.pages[
    state.labelcraft.projects[state.labelcraft.projectId].activePage
  ]?.hasUpdates;
export const selectHasManyAnnotationUpdates = (state) =>
  Object.values(
    state.labelcraft.projects[state.labelcraft.projectId]?.pages ?? {}
  ).filter((item) => item.hasUpdates).length >= 2;
